import React, { useState, useEffect } from 'react';
import { Search, MapPin, X } from 'lucide-react';

const AddressSearch = ({
  onAddressSelect,
  placeholder = "주소를 입력하세요",
  className = "",
  disabled = false,
  defaultValue = "" // 부모로부터 초기 주소값을 받음
}) => {
  const [searchQuery, setSearchQuery] = useState(defaultValue);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // 1. 외부(formData)에서 주소 값이 변경되거나 탭 전환 시 텍스트 동기화
  useEffect(() => {
    setSearchQuery(defaultValue || '');
  }, [defaultValue]);

  // 2. 카카오 우편번호 스크립트 로드
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // 컴포넌트 언마운트 시 필요한 정리가 있다면 여기에 작성
    };
  }, []);

  // 주소 검색 팝업 열기
  const openPostcode = () => {
    if (disabled) return;

    if (typeof window.daum === 'undefined') {
      alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    new window.daum.Postcode({
      oncomplete: function (data) {
        // 팝업에서 검색결과 항목을 클릭했을때 실행되는 코드
        // 도로명 주소가 없을 경우 지번 주소 사용
        const fullAddress = data.roadAddress || data.address;

        const addressData = {
          address: data.jibunAddress || data.address,
          roadAddress: data.roadAddress,
          addressName: data.address,
          zipCode: data.zonecode,
          x: data.bcode,
          y: data.bname
        };

        // 로컬 상태 업데이트
        setSelectedAddress(addressData);
        setSearchQuery(fullAddress);

        // 부모 컴포넌트(ContractFormV5)에 선택된 주소 객체 전달
        onAddressSelect(addressData);
      },
      onclose: function (state) {
        // 팝업이 닫힐 때 추가 작업이 필요한 경우
      }
    }).open();
  };

  // 검색 결과 클리어
  const clearSearch = () => {
    setSearchQuery('');
    setSelectedAddress(null);
    onAddressSelect(null);
  };

  return (
    <div className={`address-search-container relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 cursor-pointer"
          readOnly
          onClick={openPostcode} // 입력창 클릭 시에도 검색 팝업 실행
        />

        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
          {searchQuery && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // input 클릭 이벤트 전파 방지
                clearSearch();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}

          <button
            onClick={openPostcode}
            disabled={disabled}
            className="text-blue-500 hover:text-blue-700 disabled:text-gray-400"
            type="button"
          >
            <Search size={16} />
          </button>
        </div>
      </div>

      {/* 새 주소를 선택했을 때만 하단에 미리보기 카드 표시 (선택 사항) */}
      {selectedAddress && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <MapPin size={16} className="text-blue-500 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-gray-900 text-sm">
                {selectedAddress.roadAddress || selectedAddress.address}
              </div>
              {selectedAddress.zipCode && (
                <div className="text-xs text-gray-500">
                  우편번호: {selectedAddress.zipCode}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSearch;